import React from 'react';
import styled from 'styled-components';

const SetupContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Subtitle = styled.h2`
  color: #555;
  margin: 2rem 0 1rem;
  font-size: 1.5rem;
`;

const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background-color: #4285f4;
  color: white;
  border-radius: 50%;
  margin-right: 0.75rem;
  font-weight: bold;
`;

const Step = styled.div`
  margin-bottom: 2rem;
`;

const StepTitle = styled.h3`
  display: flex;
  align-items: center;
  color: #333;
  margin-bottom: 1rem;
`;

const StepContent = styled.div`
  padding-left: 3rem;
`;

const CodeBlock = styled.pre`
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  margin: 1rem 0;
`;

const Code = styled.code`
  font-family: 'Courier New', Courier, monospace;
`;

const Alert = styled.div`
  background-color: #fef7e0;
  color: #b06000;
  padding: 1rem;
  border-radius: 4px;
  margin: 1rem 0;
  font-weight: 500;
`;

const Link = styled.a`
  color: #4285f4;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const FirebaseSetup = () => {
  return (
    <SetupContainer>
      <Title>Firebase Setup Guide</Title>
      
      <p>
        To use the authentication and database features of this application, you need to set up a Firebase project
        and add your configuration to the app. Follow these steps to get started:
      </p>
      
      <Subtitle>Setting Up Firebase</Subtitle>
      
      <Step>
        <StepTitle>
          <StepNumber>1</StepNumber>
          Create a Firebase Project
        </StepTitle>
        <StepContent>
          <p>Go to the <Link href="https://console.firebase.google.com/" target="_blank" rel="noopener noreferrer">Firebase Console</Link> and create a new project.</p>
          <ul>
            <li>Click "Add project"</li>
            <li>Enter a project name</li>
            <li>Enable or disable Google Analytics as desired</li>
            <li>Click "Create project"</li>
          </ul>
        </StepContent>
      </Step>
      
      <Step>
        <StepTitle>
          <StepNumber>2</StepNumber>
          Register Your Web App
        </StepTitle>
        <StepContent>
          <p>Once your project is created, register your web application:</p>
          <ul>
            <li>Click the web icon (<code>&lt;/&gt;</code>) to add a web app</li>
            <li>Enter a nickname for your app (e.g., "Master Collectors Forge")</li>
            <li>Optionally enable Firebase Hosting</li>
            <li>Click "Register app"</li>
          </ul>
        </StepContent>
      </Step>
      
      <Step>
        <StepTitle>
          <StepNumber>3</StepNumber>
          Copy Your Firebase Configuration
        </StepTitle>
        <StepContent>
          <p>After registering, you'll see the Firebase configuration object. Copy this configuration:</p>
          <CodeBlock>
            <Code>{`
const firebaseConfig = {
  apiKey: "YOUR_API_KEY",
  authDomain: "YOUR_PROJECT_ID.firebaseapp.com",
  projectId: "YOUR_PROJECT_ID",
  storageBucket: "YOUR_PROJECT_ID.appspot.com",
  messagingSenderId: "YOUR_MESSAGING_SENDER_ID",
  appId: "YOUR_APP_ID"
};
            `}</Code>
          </CodeBlock>
        </StepContent>
      </Step>
      
      <Step>
        <StepTitle>
          <StepNumber>4</StepNumber>
          Update Your Firebase Configuration File
        </StepTitle>
        <StepContent>
          <p>Open the file at <code>src/firebase/config.js</code> and replace the placeholder configuration with your actual Firebase configuration:</p>
          <Alert>
            Important: In a production environment, you should never expose your Firebase configuration in client-side code without proper security rules. 
            Make sure to set up appropriate Firestore and Authentication security rules in the Firebase Console.
          </Alert>
        </StepContent>
      </Step>
      
      <Subtitle>Setting Up Authentication</Subtitle>
      
      <Step>
        <StepTitle>
          <StepNumber>5</StepNumber>
          Enable Authentication Methods
        </StepTitle>
        <StepContent>
          <p>In the Firebase Console, set up the authentication methods you want to use:</p>
          <ul>
            <li>Go to "Authentication" in the left sidebar</li>
            <li>Click on "Sign-in method"</li>
            <li>Enable Email/Password authentication</li>
            <li>Optionally, enable Google authentication or other providers</li>
          </ul>
        </StepContent>
      </Step>
      
      <Subtitle>Setting Up Firestore Database</Subtitle>
      
      <Step>
        <StepTitle>
          <StepNumber>6</StepNumber>
          Create a Firestore Database
        </StepTitle>
        <StepContent>
          <p>Set up Firestore to store user data and orders:</p>
          <ul>
            <li>Go to "Firestore Database" in the left sidebar</li>
            <li>Click "Create database"</li>
            <li>Start in production mode or test mode (you can change this later)</li>
            <li>Select a location closest to your users</li>
          </ul>
        </StepContent>
      </Step>
      
      <Step>
        <StepTitle>
          <StepNumber>7</StepNumber>
          Set Up Firestore Security Rules
        </StepTitle>
        <StepContent>
          <p>Configure security rules to protect your data:</p>
          <CodeBlock>
            <Code>{`
rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    // Allow authenticated users to read and write their own data
    match /users/{userId} {
      allow read, write: if request.auth != null && request.auth.uid == userId;
      
      // Allow users to read and write their own orders
      match /orders/{orderId} {
        allow read, write: if request.auth != null && request.auth.uid == userId;
      }
      
      // Allow users to read and write their own settings
      match /settings/{document=**} {
        allow read, write: if request.auth != null && request.auth.uid == userId;
      }
    }
    
    // Allow authenticated users to create orders
    match /orders/{orderId} {
      allow create: if request.auth != null;
      allow read: if request.auth != null && request.resource.data.userId == request.auth.uid;
    }
  }
}
            `}</Code>
          </CodeBlock>
        </StepContent>
      </Step>
      
      <Subtitle>Final Steps</Subtitle>
      
      <Step>
        <StepTitle>
          <StepNumber>8</StepNumber>
          Restart Your Application
        </StepTitle>
        <StepContent>
          <p>After updating your Firebase configuration, restart your React application to apply the changes.</p>
        </StepContent>
      </Step>
      
      <Alert>
        Remember to never commit your actual Firebase configuration to public repositories. Consider using environment variables for production deployments.
      </Alert>
    </SetupContainer>
  );
};

export default FirebaseSetup;
